import clsx from "clsx";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { BottomSheet } from "../../../BottomSheet";
import { Button as ButtonV2 } from "../../../Button";
import { InfoIcon } from "../../../Icons";
import { Typo } from "../../../Typo";

type MobileInfoTriggerProps = {
  label: string;
  text: string;
  isCompact?: boolean;
};

/**
 * Mobile "i" tooltip trigger — opens a full BottomSheet instead of the
 * desktop hover tooltip. Local to the Shared Rebate Programs mobile view
 * (PART-4052 §4.4 decision: keep local rather than update the shared,
 * pepper-ui-kit-backed `ResponsiveInfoTooltip`).
 */
export const MobileInfoTrigger = ({
  label,
  text,
  isCompact = false,
}: MobileInfoTriggerProps) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return (
    <>
      <button
        type="button"
        data-testid="mobile_info_trigger_button"
        aria-label={t("subIBReport_infoAbout", "About {{label}}", { label })}
        // The icon stays visually small (h-3.5); the ::before extends the tap
        // target to the 44x44 minimum (handoff spec §7) without affecting
        // layout or the parent row's height.
        className={clsx(
          "relative inline-flex shrink-0 items-center justify-center before:absolute before:-inset-[14px] before:content-['']",
          isCompact ? "h-3 w-3" : "h-4 w-4",
        )}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <InfoIcon
          className={clsx(
            "shrink-0 text-text-secondary",
            isCompact ? "h-3 w-3" : "h-3.5 w-3.5",
          )}
          aria-hidden="true"
        />
      </button>
      <BottomSheet
        isOpen={open}
        onClose={() => setOpen(false)}
        title={label}
        isDraggable
        footer={
          <ButtonV2
            variant="brand"
            size="md"
            className="w-full"
            onClick={() => setOpen(false)}
          >
            {t("subIBReport_infoGotIt", "Got it")}
          </ButtonV2>
        }
      >
        <Typo element="span" type="bodySM" textColor="text-text-primary">
          {text}
        </Typo>
      </BottomSheet>
    </>
  );
};
